import React, { useState } from 'react'
import { contactAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import {
  HiOutlineEnvelope,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlinePaperAirplane,
  HiOutlineCheckCircle,
  HiOutlineMapPin,
} from 'react-icons/hi2'
import { FaWhatsapp } from 'react-icons/fa'
import LegalLayout from './legal/LegalLayout'
import { COMPANY, OVACS_SUPPORT } from '../../config/brand'

const ContactPage = () => {
  const { showSuccess, showError } = useAlerts()
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    category: 'contact_visitor',
    subject: '',
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.sender_name || !formData.sender_email || !formData.subject || !formData.message) {
      showError('Please fill in all required fields')
      return
    }

    try {
      setLoading(true)
      const response = await contactAPI.submitContactMessage(formData)
      
      if (response.success) {
        showSuccess('Message sent successfully! We\'ll get back to you soon.')
        setSubmitted(true)
        setFormData({
          sender_name: '',
          sender_email: '',
          sender_phone: '',
          category: 'contact_visitor',
          subject: '',
          message: ''
        })
      } else {
        showError('Failed to send message. Please try again.')
      }
    } catch (error) {
      console.error('Error sending message:', error)
      showError('Failed to send message. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <LegalLayout title="Contact Us">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiOutlineCheckCircle className="w-10 h-10 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us. We'll get back to you as soon as possible.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Send Another Message
          </button>
        </div>
      </LegalLayout>
    )
  }

  return (
    <LegalLayout title="Contact Us" description="Contact OVACS by email, phone, WhatsApp or post." wide>
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Us</h1>
          <p className="text-gray-600">
            Have a question or feedback? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
        {/* Contact details */}
        <aside className="space-y-5 text-sm text-gray-700 lg:order-2">
          <ContactDetail icon={HiOutlineEnvelope} label="Email">
            <a href={`mailto:${OVACS_SUPPORT.email}`} className="text-cyan-700 underline" dir="ltr">{OVACS_SUPPORT.email}</a>
          </ContactDetail>
          <ContactDetail icon={HiOutlinePhone} label="Phone">
            <a href={`tel:${OVACS_SUPPORT.phone.replace(/\s/g, '')}`} className="text-cyan-700 underline" dir="ltr">{OVACS_SUPPORT.phone}</a>
          </ContactDetail>
          <ContactDetail icon={FaWhatsapp} label="WhatsApp">
            <a href={OVACS_SUPPORT.whatsappLink} target="_blank" rel="noopener noreferrer" className="text-cyan-700 underline" dir="ltr">{OVACS_SUPPORT.phone}</a>
          </ContactDetail>
          <ContactDetail icon={HiOutlineMapPin} label="Postal address">
            <span>{COMPANY.legalName}<br />{COMPANY.address}</span>
          </ContactDetail>
        </aside>

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-lg p-8 lg:col-span-2 lg:order-1">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <HiOutlineUser className="inline w-4 h-4 mr-1" />
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.sender_name}
                onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Your name"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <HiOutlineEnvelope className="inline w-4 h-4 mr-1" />
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.sender_email}
                onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="your.email@example.com"
                required
              />
            </div>

            {/* Phone (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <HiOutlinePhone className="inline w-4 h-4 mr-1" />
                Phone (Optional)
              </label>
              <input
                type="tel"
                value={formData.sender_phone}
                onChange={(e) => setFormData({ ...formData, sender_phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="+1 234 567 890"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              >
                <option value="contact_visitor">General Contact</option>
                <option value="bug_report">Bug Report</option>
                <option value="suggestion">Suggestion</option>
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Brief subject of your message"
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="6"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Your message..."
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg font-medium"
            >
              <HiOutlinePaperAirplane className="w-5 h-5" />
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>
            We aim to reply within 2 working days.
          </p>
        </div>
    </LegalLayout>
  )
}

const ContactDetail = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-cyan-700" />
    </div>
    <div>
      <p className="font-semibold text-gray-900">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  </div>
)

export default ContactPage

